import { type ReactNode, type Ref, forwardRef } from 'react';
import {
    Box,
    Img,
    Paper,
    Skeleton,
    Stack,
    type Theme,
    Typography,
    type TypographyProps,
    styled,
    useTheme,
} from '@lingoda/ui';
import { trans } from '@lingoda/i18n';
import { CaretDownIcon, CaretUpIcon } from '@lingoda/ui/icons/lingodaIcons';
import knownStatImage from './assets/vocab-known.svg?url';
import reviewingStatImage from './assets/vocab-reviewing.svg?url';

interface StyleProps {
    backgroundColor: string;
    textColor?: string;
    counterColor?: string;
    statImage?: string;
}

interface DataProps {
    title?: string;
    count?: ReactNode;
    difCount?: number;
    indicatorRef?: Ref<HTMLElement>;
    indicatorLabel?: ReactNode;
    indicatorSx?: IndicatorProps['sx'];
    backgroundAnimation?: ReactNode;
}

type Props = StyleProps & DataProps;

export const ReviewingStat = (props: DataProps) => {
    const theme = useTheme();

    return <Stat {...getReviewingStatProps(theme)} {...props} />;
};

export const KnownStat = (props: DataProps) => {
    const theme = useTheme();

    return <Stat {...getKnownStatProps(theme)} {...props} />;
};

export const ReviewingStatSkeleton = (props: DataProps) => {
    const theme = useTheme();

    return <StatSkeleton {...getReviewingStatProps(theme)} {...props} />;
};

export const KnownStatSkeleton = (props: DataProps) => {
    const theme = useTheme();

    return <StatSkeleton {...getKnownStatProps(theme)} {...props} />;
};

const getKnownStatProps = (theme: Theme) => {
    return {
        textColor: theme.colors.text.success,
        backgroundColor: theme.palette.background.paper,
        counterColor: theme.palette.text.primary,
        title: trans('Known', {}, 'quiz-practice-dashboard'),
        statImage: knownStatImage,
    };
};

const getReviewingStatProps = (theme: Theme) => {
    return {
        textColor: theme.colors.text.warning,
        backgroundColor: theme.palette.background.paper,
        counterColor: theme.palette.text.primary,
        title: trans('Reviewing', {}, 'quiz-practice-dashboard'),
        statImage: reviewingStatImage,
    };
};

const Stat = ({
    backgroundAnimation,
    backgroundColor,
    title,
    count,
    difCount,
    textColor,
    counterColor,
    indicatorRef,
    indicatorLabel,
    indicatorSx,
    statImage,
}: Props) => {
    return (
        <StatPaper variant="outlined" backgroundColor={backgroundColor}>
            {backgroundAnimation}
            <BackgroundImage src={statImage} aria-hidden="true" />
            <Box position="relative">
                <Typography variant="subtitle2" htmlColor={textColor}>
                    {title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box color={counterColor}>{count}</Box>

                    {difCount ? (
                        <Indicator
                            ref={indicatorRef}
                            sx={indicatorSx}
                            htmlColor={textColor}
                            count={difCount}
                            label={indicatorLabel}
                        />
                    ) : null}
                </Stack>
            </Box>
        </StatPaper>
    );
};

const StatSkeleton = ({ backgroundColor, textColor }: Props) => {
    return (
        <StatPaper backgroundColor={backgroundColor}>
            <Box>
                <Typography variant="subtitle2" htmlColor={textColor}>
                    <Skeleton variant="text" width="100px" />
                </Typography>
                <Skeleton variant="text" width="70px" height="56px" />
            </Box>
        </StatPaper>
    );
};

const StatPaper = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor: string }>(({ theme, backgroundColor }) => ({
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2, 2),
    },
    height: '108px',
    backgroundColor,
    boxShadow: 'none',
}));

const BackgroundImage = styled(Img)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        right: '0.5rem',
    },
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

export const StatCount = (props: Pick<TypographyProps, 'component' | 'children' | 'htmlColor'>) => {
    return (
        <Typography
            variant="h2"
            fontSize={{ xs: '32px', smw: '40px', md: '48px' }}
            fontWeight={700}
            lineHeight={['56px']}
            {...props}
        />
    );
};

interface IndicatorProps {
    htmlColor?: string;
    count: number;
    label?: ReactNode;
    sx?: TypographyProps['sx'];
}

const Indicator = forwardRef<HTMLElement, IndicatorProps>(
    ({ htmlColor, count, label, sx }, ref) => (
        <Typography
            ref={ref}
            variant="subtitle2"
            htmlColor={htmlColor}
            display="flex"
            alignItems="center"
            sx={sx}
        >
            {count > 0 ? <CaretUpIcon size={20} /> : <CaretDownIcon size={20} />} {Math.abs(count)}{' '}
            {label}
        </Typography>
    ),
);
Indicator.displayName = 'Indicator';
